// ============================================================================
// LE MOTIF D'UN GESTE QUI ENGAGE — ouvrir le back-office d'un client, réinitialiser ou supprimer un magasin.
//
// ⚠️ LE MOTIF EST OBLIGATOIRE ET VA AU JOURNAL : la fiche magasin a une section « Historique »,
//    et c'est ce texte qu'on y relira. Le serveur le REFUSE vide (`reason_required`) — la modale n'est que la première
//    garde, jamais la seule.
// ⚠️ RECOPIER LE NOM pour ce qui ne se rattrape pas (réinitialiser, supprimer) : un clic se donne par réflexe, recopier
//    oblige à regarder CE qu'on détruit. Le serveur revérifie la recopie lui aussi.
// ============================================================================
import { useId, useState, type ReactNode } from 'react';
import { alert, useOverlayPanel } from '@orgazone/ui-web';
import { openStoreBackoffice } from '../cross-tenant';

/** Le nombre de caractères sous lequel un motif ne dit rien (« ok », « test »). */
export const MIN_REASON_LENGTH = 5;

export function ReasonModal({ title, intro, children, requireName, confirmLabel, busyLabel, danger, placeholder, onClose, onConfirm }: {
  title: string;
  /** L'exemple de motif — défaut : celui d'un geste sur un magasin. */
  placeholder?: string;
  /** Ce que le geste fait, en une phrase sous le titre. */
  intro: string;
  /** Les conséquences détaillées, s'il y en a. */
  children?: ReactNode;
  /** Le nom à recopier pour confirmer — absent : le motif suffit. */
  requireName?: string;
  confirmLabel: string;
  busyLabel: string;
  danger?: boolean;
  onClose: () => void;
  /**
   * Le geste, le motif en main. Il rend `true` quand il a abouti (la modale se ferme), `false` sinon (elle reste ouverte
   * pour corriger). ⚠️ Appelé DANS le clic : une ouverture d'onglet doit précéder tout `await`.
   */
  onConfirm: (reason: string) => Promise<boolean>;
}) {
  const titleId = useId();
  const reasonId = useId();
  const nameId = useId();
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  // ⚠️ Échap ferme et le focus revient au déclencheur ; refusé pendant le geste, comme le clic sur le fond.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  const reasonOk = reason.trim().length >= MIN_REASON_LENGTH;
  const nameOk = requireName === undefined || typed.trim() === requireName;
  const ready = reasonOk && nameOk && !busy;

  const submit = () => {
    if (!ready) return;
    setBusy(true);
    void onConfirm(reason.trim()).then((done) => { if (done) onClose(); else setBusy(false); });
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={{ width: 'min(94vw, 480px)' }} onClick={(e) => e.stopPropagation()}>
        <h3 id={titleId} style={{ marginBottom: 4 }}>{title}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12, lineHeight: 1.5 }}>{intro}</div>
        {children}
        <label htmlFor={reasonId} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', margin: '4px 0 6px' }}>
          Motif <span style={{ color: 'var(--wa-fg)', fontWeight: 500 }}>(obligatoire — il entre au journal)</span>
        </label>
        <textarea id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          placeholder={placeholder ?? 'Pourquoi ce geste — il sera relu tel quel dans l’historique du magasin.'}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${reasonOk ? 'var(--ink200)' : 'var(--wa-fg)'}`, background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', resize: 'vertical', outline: 'none' }} />
        {requireName !== undefined && (
          <>
            <label htmlFor={nameId} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', margin: '12px 0 6px' }}>
              Recopiez le nom du magasin : <b className="mono" style={{ color: 'var(--ink900)' }}>{requireName}</b>
            </label>
            <input id={nameId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off"
              style={{ width: '100%', height: 36, padding: '0 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${nameOk ? 'var(--ok-fg)' : 'var(--ink200)'}`, background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)' }} />
          </>
        )}
        <div className="modalfoot">
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={onClose} disabled={busy}>Annuler</button>
          <button className={danger ? 'btn danger' : 'btn btn-cta'} style={{ width: 'auto', margin: 0, padding: '9px 18px' }} disabled={!ready} onClick={submit}>
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Ouvrir le back-office d'un magasin client, motif en main (session de support de 8 h, journalisée).
 * ⚠️ « En lecture » n'est PAS promis : le rôle du support au back-office porte aujourd'hui la lecture ET l'écriture
 *    (contradiction signalée au SPEC tronc §24.9quinquies c, à trancher). La modale dit ce qui se passe réellement.
 */
export function OpenBackofficeModal({ store, onClose }: { store: { id: string; name: string }; onClose: () => void }) {
  return (
    <ReasonModal
      title={`Ouvrir le back-office de « ${store.name} »`}
      intro="Avec votre compte Orgazone, dans un nouvel onglet, borné à ce magasin. La session dure 8 heures au plus ; elle apparaît, avec votre motif, dans le journal d’accès du client et dans l’historique du magasin."
      confirmLabel="Ouvrir le back-office"
      busyLabel="Ouverture…"
      onClose={onClose}
      onConfirm={async (reason) => {
        let failed = false;
        await openStoreBackoffice(store.id, store.name, reason, (m) => { failed = true; void alert({ title: 'Ouverture du back-office impossible', message: m, tone: 'err' }); });
        return !failed;
      }}
    />
  );
}
